<template>
    <QueryLoaderGuard
        :is-pending="isPending"
        :is-error="isError"
    >
        <template v-if="hasRecords">
            <template v-for="analysis in analysisPanels" :key="analysis.key">
                <template v-if="data && data.analysisImages && data.analysisImages[analysis.key]">
                    <ReportImagesMeasurementPanel
                        :id="analysis.key"
                        :report-number="reportNumber"
                        :title="t(analysis.titleKey)"
                        :images="data.analysisImages[analysis.key]"
                    />
                </template>
            </template>
        </template>

        <EmptyStatePanelContent v-else />
    </QueryLoaderGuard>
</template>

<script setup lang="ts">
import { computed, nextTick, toRefs, watch } from 'vue'
import { useListAnalysisImagesQuery } from '@/api/queries/images/useListAnalysisImagesQuery'
import ReportImagesMeasurementPanel from '@/pages/generators/report-images/sections/ReportImagesMeasurementPanel.vue'
import { MeasurementType, MeasurementTypes } from '@/configuration/measurements/measurement-types'
import EmptyStatePanelContent from '@/components/states/EmptyStatePanelContent.vue'
import { useI18n } from 'vue-i18n'
import QueryLoaderGuard from '@/components/guards/QueryLoaderGuard.vue'
import { scrollToElement } from '@/utils/scroll-utils'
import { useRoute, useRouter } from 'vue-router'
import { AnalysisType, AnalysisTypes } from '@/configuration/analysis/analysis-types'

const props = defineProps<{
    componentSid: string
    reportNumber: string
}>()
const { componentSid, reportNumber } = toRefs(props)

const route = useRoute()
const router = useRouter()

const { t } = useI18n()

const { data, isPending, isError } = useListAnalysisImagesQuery(reportNumber.value, componentSid.value)

const hasRecords = computed(() => !!data.value?.analysisImages && Object.keys(data.value.analysisImages).length > 0)

const processScroll = async () => {
    if (!route.query.focused) {
        return
    }

    await nextTick()
    scrollToElement(route.query.focused.toString())

    router.replace({
        query: {},
    })
}

watch(hasRecords, (newHasRecords) => {
    if (!newHasRecords) {
        return
    }

    processScroll()
}, { immediate: true })

const analysisPanels = [
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.Typenschild)?.labelKey ?? '',
        key: MeasurementType.Typenschild,
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.WindingResistance)?.labelKey ?? '',
        key: MeasurementType.WindingResistance,
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.PoleImpedance)?.labelKey ?? '',
        key: MeasurementType.PoleImpedance,
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.IsolationImpedanceStator)?.labelKey ?? '',
        key: MeasurementType.IsolationImpedanceStator,
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.IsolationImpedanceRotor)?.labelKey ?? '',
        key: MeasurementType.IsolationImpedanceRotor,
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.LossFactor)?.labelKey ?? '',
        key: MeasurementType.LossFactor,
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.PartialDischarge)?.labelKey ?? '',
        key: MeasurementType.PartialDischarge,
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.VoltageTesting)?.labelKey ?? '',
        key: MeasurementType.VoltageTesting,
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.InductionTesting)?.labelKey ?? '',
        key: MeasurementType.InductionTesting,
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.HeatingTest)?.labelKey ?? '',
        key: MeasurementType.HeatingTest,
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.Vibrations)?.labelKey ?? '',
        key: MeasurementType.Vibrations,
    },
    {
        titleKey: AnalysisTypes.find((e) => e.value === AnalysisType.VisualInspection)?.labelKey ?? '',
        key: AnalysisType.VisualInspection,
    },
]
</script>

<style scoped lang="scss">
.empty-state {
    padding: 50px 0;

    display: flex;
    flex-direction: column;

    align-items: center;
    justify-content: center;
    gap: $spacing-s;

    color: $pbl-text-muted;

    pbl-icon {
        --size: 2rem;
    }

    span {
        font-weight: 500;
        font-size: 1.25rem;
    }
}
</style>